import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import moment, { Moment } from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MomentHelper } from '../helpers/moment-helper';
import { AttributeFilters } from '../models/attribute-filters';
import { PropertyType } from '../models/enums/property-type';
import { FilterCriteria } from '../models/filter-criteria';
import { IFilterable } from '../models/interfaces/filterable.interface';
import { PredefinedFilter } from '../models/predefined-filter';
import { AttributeDefinition } from '../models/server/attribute-definition';

/**
 * Gestionnaire des filtres de la liste des dossiers.
 * S'applique sur n'importe quel attribut d'un dossier.
 */
export class FoldersListFilterHandler {
    /** Utilisé pour gérer la désinscription des observables lorsque la classe est détruite. */
    protected classDisposed$: Subject<void> = new Subject<void>();

    /** Formulaire contenant les filtres sur les attributs des dossiers. */
    filtersForm: UntypedFormGroup;

    /** Dictionaire contenant les Valeurs des filtres de chaque attribut. */
    filterValues: AttributeFilters = new AttributeFilters();

    /** Liste des attributs pouvant être filtrés */
    filterableAttributes: AttributeDefinition[];

    /**
     * Constructeur.
     * @param sourceComponent : Composant source utilisé pour appliquer les filtres.
     */
    constructor(private sourceComponent: IFilterable) {
        this.filtersForm = new UntypedFormGroup({});
    }

    /**
     * Initialise les filtres pour chaque attributs des dossiers.
     * @param attributeDefinitions : Définitions des attributs à associer à un filtre.
     */
    public initializeAttributeFilters(attributeDefinitions: AttributeDefinition[], defaultFilter: PredefinedFilter) {
        this.filterableAttributes = attributeDefinitions;

        // Crée et intialise un contrôle de formulaire pour gerer le filtre de chaque attributs.
        this.filterableAttributes.forEach((att) => {
            this.initializeAttributeFilter(
                att,
                defaultFilter?.filteredAttributes && defaultFilter?.filteredAttributes.hasKey(att.propertyCode) ? defaultFilter.filteredAttributes.get(att.propertyCode) : null
            );
        });
    }

    /**
     * Initialise le filtre associé à un attribut.
     * @param attributeDefinition: Definition de l'attribut associé au filtre.
     */
    private initializeAttributeFilter(attributeDefinition: AttributeDefinition, defaultFilterCriteria: FilterCriteria) {
        // Création du contrôle de formulaire associé à l'attribut.
        this.filtersForm.addControl(attributeDefinition.propertyCode, new UntypedFormControl());

        if (defaultFilterCriteria) {
            let defaultValue: string | string[] | number[] = null;

            if (attributeDefinition.type === PropertyType.List || attributeDefinition.type === PropertyType.ListCustomers || attributeDefinition.type === PropertyType.ListUsers) {
                // En cas d'attribut "liste" retourne un tableau de string
                defaultValue = defaultFilterCriteria.values;
            } else if (attributeDefinition.type === PropertyType.Color) {
                // En cas d'attribut "couleur" retourne un tableau d'entier
                let numberValues = new Array<number>();
                defaultFilterCriteria.values.forEach((x) => numberValues.push(Number(x)));
                defaultValue = numberValues;
            } else if (defaultFilterCriteria.values != null) {
                // En cas d'attribut "classique" retourne la 1ere valeur du tableau
                defaultValue = defaultFilterCriteria.values[0];
            } else {
                defaultValue = null;
            }

            this.filtersForm.controls[attributeDefinition.propertyCode].setValue(defaultValue);

            // Initialisation de la valeur du filtre associé à l'attribut.
            this.filterValues.set(attributeDefinition.propertyCode, defaultFilterCriteria);
        } else {
            // Initialisation de la valeur du filtre associé à l'attribut.
            this.filterValues.remove(attributeDefinition.propertyCode);
        }

        // S'abonne aux evenements de changement de valeur du contrôle de formulaire associé à cet attribut.
        this.subscribeToFilterValueChange(attributeDefinition);
    }

    /**
     * S'abonne aux evenements de changement de valeur d'un filtre d'attributs.
     * @param attributeDefinition : Definition de l'attribut associé au filtre pour lequel on désire détecter les changements de valeur.
     */
    private subscribeToFilterValueChange(attributeDefinition: AttributeDefinition) {
        this.filtersForm.controls[attributeDefinition.propertyCode].valueChanges.pipe(takeUntil(this.classDisposed$)).subscribe((val) => {
            if (attributeDefinition.type === PropertyType.Date) {
                // Ne se declenche que si la saisie est une date valide
                if (!!val && moment.isMoment(val)) {
                    this.applyAttributeFilter(attributeDefinition, val);
                }
            } else {
                if (!!val && val.length != 0) {
                    // On applique le filtre dès que l'utilisateur saisie une valeur.
                    this.applyAttributeFilter(attributeDefinition, val);
                } else if (this.filterValues.hasKey(attributeDefinition.propertyCode)) {
                    // Si le texte saisi est vide mais que la valeur du filtre n'est pas vide.
                    this.resetAttributeFilter(attributeDefinition.propertyCode);
                }
            }
        });
    }

    /**
     * Applique un filtre.
     * @param attributeDefinition: Definition de l'attribut sur lequel s'applique le filtre.
     * @param filterValue: Valeur du filtre saisi par l'utilisateur.
     */
    private applyAttributeFilter(attributeDefinition: AttributeDefinition, filterValue: any) {
        if (attributeDefinition.type === PropertyType.Date) {
            filterValue = MomentHelper.formatAsIso8601Date(filterValue as Moment);
        } else if (attributeDefinition.type === PropertyType.DateAndTime) {
            filterValue = MomentHelper.formatAsIso8601DateTime(filterValue as Moment);
        }

        let array: string[] = [];

        if (!Array.isArray(filterValue)) {
            array.push(filterValue.toString());
        } else {
            array = filterValue;
        }

        const filterCriteria = new FilterCriteria(attributeDefinition.type, array);
        this.filterValues.set(attributeDefinition.propertyCode, filterCriteria);

        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise les filtres de tous les attributs puis applique eventuellement les filtres.
     * @param applyAfterReset : indique si les filtres doivent être appliqués après la reinitialisation.
     */
    public resetAllAttributesFilters(applyAfterReset: boolean): void {
        if (!!this.filterableAttributes) {
            this.filterableAttributes.forEach((item) => {
                this.filterValues.remove[item.propertyCode];
            });
        }

        this.filtersForm.reset();

        if (applyAfterReset === true) {
            this.sourceComponent.applyFilters();
        }
    }

    /**
     * Réinitialise le filtre de l'attribut spécifiée puis applique le filtre.
     * @param attributeName: Nom de l'attribut pour lequel le filtre doit être réinitialisé.
     */
    public resetAttributeFilter(attributeName: string): void {
        this.filterValues.remove(attributeName);
        this.filtersForm.controls[attributeName].reset();
        this.sourceComponent.applyFilters();
    }

    /**
     * Specifie les filtres par défaut.
     * @param filters les filtres à appliquer
     */
    public setDefaultFilters(filters: AttributeFilters) {
        this.filterValues = filters;
    }

    /**
     * Récupère les filtres saisis
     * @retourne Les filtres saisis
     */
    public getFilters(): AttributeFilters {
        return this.filterValues;
    }

    /**
     * Appelé pour désinscrire les observables.
     */
    public dispose() {
        this.classDisposed$.next();
        this.classDisposed$.complete();
    }
}
